import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Building2, Plus, Loader2, Search, Globe, MapPin, Users, Target,
  Trash2, ChevronLeft, ChevronRight, Mail, Phone, ExternalLink, Upload, ListPlus, X, Send, Share2, Newspaper, RefreshCw, UserSearch, UserPlus, ShieldCheck
} from 'lucide-react';
import MigrationBanner from '../components/MigrationBanner';
import ActivityTimeline from '../components/ActivityTimeline';
import EmailHistory from '../components/EmailHistory';
import CustomFields from '../components/CustomFields';
import TagsLifecycle from '../components/TagsLifecycle';
import ImportDialog from '../components/ImportDialog';
import AddToListDialog from '../components/AddToListDialog';
import {
  RecordTypeBadge, SupplierTagBadges, CareSegmentEditor, CareSegmentBulkDialog,
  RECORD_TYPES, SUPPLIER_TAGS,
} from '../components/CareSegment';
import { ShareToOrgDialog } from '../components/ShareToOrgDialog';
import { useOrg } from '../context/OrgContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTOR_COLORS = {
  care_provider: 'border-emerald-700/40 text-emerald-400',
  health_tech: 'border-blue-700/40 text-blue-400',
  care_tech: 'border-violet-700/40 text-violet-400',
  charity: 'border-amber-700/40 text-amber-400',
};

const Companies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [grandfathering, setGrandfathering] = useState(false);

  const handleGrandfather = async () => {
    if (!window.confirm('Grandfather all existing paying suppliers: mark their current products as already-unlocked (so none are charged the £200 fee) and set one primary listing each. Safe & idempotent.')) return;
    setGrandfathering(true);
    const tid = toast.loading('Grandfathering existing products…');
    try {
      const { data } = await axios.post(`${API}/admin/directory/grandfather-products`);
      toast.success(`Done: ${data.suppliers_updated || 0} suppliers, ${data.products_unlocked || 0} products unlocked.`, { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Grandfather failed', { id: tid });
    } finally {
      setGrandfathering(false);
    }
  };

  const handlePublishLive = async () => {
    if (!window.confirm('Publish the bundled directory + Knowledge Hub + podcast content to this environment\u2019s live database? This is safe and non-destructive (it merges, never deletes).')) return;
    setPublishing(true);
    const tid = toast.loading('Publishing content to live\u2026');
    try {
      const { data } = await axios.post(`${API}/admin/directory/sync-import`);
      const s = data.summary || {};
      toast.success(`Published: ${s.updated || 0} suppliers updated, ${s.created || 0} added, ${s.blog_posts_upserted || 0} articles, ${s.products_added || 0} products.`, { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publish failed', { id: tid });
    } finally {
      setPublishing(false);
    }
  };
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sectors, setSectors] = useState([]);
  const [search, setSearch] = useState('');
  const [filterSector, setFilterSector] = useState('all');
  const [filterRecordType, setFilterRecordType] = useState('all');
  const [filterSupplierTag, setFilterSupplierTag] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showAddToList, setShowAddToList] = useState(false);
  const [showBulkSegment, setShowBulkSegment] = useState(false);
  const [showShareOrg, setShowShareOrg] = useState(false);
  const [emailingSegment, setEmailingSegment] = useState(false);
  const { orgs, activeOrgId } = useOrg();
  const [newCompany, setNewCompany] = useState({
    name: '', domain: '', website: '', location: '', industry: '', sector: 'none', sub_segment: 'none',
  });

  const fetchSectors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sectors`);
      setSectors(res.data.sectors || []);
    } catch { /* ignore */ }
  }, []);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (search) params.search = search;
      if (filterSector !== 'all') params.sector = filterSector;
      if (filterRecordType !== 'all') params.record_type = filterRecordType;
      if (filterSupplierTag !== 'all') params.supplier_tag = filterSupplierTag;
      const res = await axios.get(`${API}/companies`, { params });
      setCompanies(res.data.companies || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      toast.error('Failed to load companies');
    } finally { setLoading(false); }
  }, [page, search, filterSector, filterRecordType, filterSupplierTag]);

  useEffect(() => { fetchSectors(); }, [fetchSectors]);
  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  // Open a specific company when navigated to with ?open=<id> (cross-linking)
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      setActiveId(openId);
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const subSegmentsFor = (sectorValue) => sectors.find(s => s.value === sectorValue)?.sub_segments || [];

  const segmentActive = filterRecordType !== 'all' || filterSupplierTag !== 'all';

  const emailSegment = async () => {
    setEmailingSegment(true);
    try {
      const body = {};
      if (filterRecordType !== 'all') body.record_type = filterRecordType;
      if (filterSupplierTag !== 'all') body.supplier_tag = filterSupplierTag;
      const res = await axios.post(`${API}/care-segments/email-segment`, body);
      toast.success(`Campaign created for ${res.data.member_count} contacts`);
      navigate(`/admin/campaigns/${res.data.campaign_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create campaign');
      setEmailingSegment(false);
    }
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCompany.name.trim()) { toast.error('Company name required'); return; }
    setCreating(true);
    try {
      const payload = {
        name: newCompany.name.trim(),
        domain: newCompany.domain || null,
        website: newCompany.website || null,
        location: newCompany.location || null,
        industry: newCompany.industry || null,
        sector: newCompany.sector !== 'none' ? newCompany.sector : null,
        sub_segment: newCompany.sub_segment !== 'none' ? newCompany.sub_segment : null,
      };
      const res = await axios.post(`${API}/companies`, payload);
      toast.success(`Company "${res.data.name}" created`);
      setShowCreate(false);
      setNewCompany({ name: '', domain: '', website: '', location: '', industry: '', sector: 'none', sub_segment: 'none' });
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create company');
    } finally { setCreating(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="companies-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl text-white mb-2 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-violet-400" />
            Companies
          </h1>
          <p className="text-slate-400">Your accounts - every organisation in one place.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="bg-emerald-950/40 border-emerald-700 text-emerald-200 hover:bg-emerald-900/40" onClick={handlePublishLive} disabled={publishing} data-testid="publish-content-live-btn">
            {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Publish content to live
          </Button>
          <Button variant="outline" className="bg-amber-950/40 border-amber-700 text-amber-200 hover:bg-amber-900/40" onClick={handleGrandfather} disabled={grandfathering} data-testid="grandfather-products-btn">
            {grandfathering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />} Grandfather products
          </Button>
          <Button variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={() => setShowImport(true)} data-testid="import-companies-btn">
            <Upload className="w-4 h-4 mr-2" /> Import
          </Button>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-500" data-testid="create-company-btn">
              <Plus className="w-4 h-4 mr-2" /> New Company
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-white">New Company</DialogTitle>
              <DialogDescription className="text-slate-400">Add an account to your CRM.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3 mt-2">
              <div><Label className="text-slate-300">Name *</Label>
                <Input value={newCompany.name} onChange={e => setNewCompany({ ...newCompany, name: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="e.g. Acme Care Group Ltd" data-testid="new-company-name" required />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-slate-300">Domain</Label>
                  <Input value={newCompany.domain} onChange={e => setNewCompany({ ...newCompany, domain: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="acmecare.co.uk" />
                </div>
                <div><Label className="text-slate-300">Location</Label>
                  <Input value={newCompany.location} onChange={e => setNewCompany({ ...newCompany, location: e.target.value })} className="bg-slate-950 border-slate-800" />
                </div>
              </div>
              <div><Label className="text-slate-300">Industry</Label>
                <Input value={newCompany.industry} onChange={e => setNewCompany({ ...newCompany, industry: e.target.value })} className="bg-slate-950 border-slate-800" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-slate-300">Sector</Label>
                  <Select value={newCompany.sector} onValueChange={v => setNewCompany({ ...newCompany, sector: v, sub_segment: 'none' })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue placeholder="-" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="none">- none -</SelectItem>
                      {sectors.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-slate-300">Sub-segment</Label>
                  <Select value={newCompany.sub_segment} onValueChange={v => setNewCompany({ ...newCompany, sub_segment: v })} disabled={newCompany.sector === 'none'}>
                    <SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue placeholder="-" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="none">- none -</SelectItem>
                      {subSegmentsFor(newCompany.sector).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter className="gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-500" disabled={creating} data-testid="submit-new-company">
                  {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4 mr-2" /> Create</>}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <MigrationBanner onDone={fetchCompanies} />

      {/* Bulk selection toolbar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-violet-950/30 border border-violet-800/40 rounded-lg px-4 py-2.5" data-testid="companies-bulk-toolbar">
          <span className="text-sm text-violet-200">{selected.size} selected</span>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-500" onClick={() => setShowAddToList(true)} data-testid="bulk-add-to-list-btn">
            <ListPlus className="w-4 h-4 mr-1.5" /> Add to list
          </Button>
          <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={() => setShowBulkSegment(true)} data-testid="bulk-segment-btn">
            <Target className="w-4 h-4 mr-1.5" /> Segment
          </Button>
          {orgs.length > 0 && !activeOrgId && (
            <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={() => setShowShareOrg(true)} data-testid="bulk-share-org-btn">
              <Share2 className="w-4 h-4 mr-1.5" /> Share to sub-account
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => setSelected(new Set())} data-testid="clear-selection-btn">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search companies..."
            className="bg-slate-900/50 border-slate-800 pl-9"
            data-testid="companies-search"
          />
        </div>
        <Select value={filterSector} onValueChange={(v) => { setFilterSector(v); setPage(1); }}>
          <SelectTrigger className="bg-slate-900/50 border-slate-800 w-[180px]" data-testid="companies-sector-filter">
            <SelectValue placeholder="All sectors" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all">All sectors</SelectItem>
            {sectors.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterRecordType} onValueChange={(v) => { setFilterRecordType(v); setPage(1); }}>
          <SelectTrigger className="bg-slate-900/50 border-slate-800 w-[160px]" data-testid="companies-record-type-filter">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all">All types</SelectItem>
            {RECORD_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSupplierTag} onValueChange={(v) => { setFilterSupplierTag(v); setPage(1); }}>
          <SelectTrigger className="bg-slate-900/50 border-slate-800 w-[170px]" data-testid="companies-supplier-tag-filter">
            <SelectValue placeholder="All supplier tags" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all">All supplier tags</SelectItem>
            {SUPPLIER_TAGS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {segmentActive && (
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={emailSegment}
            disabled={emailingSegment}
            data-testid="email-segment-btn"
          >
            {emailingSegment ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            Email this segment
          </Button>
        )}
        <span className="text-sm text-slate-500" data-testid="companies-total">{total.toLocaleString()} companies</span>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20" data-testid="companies-loading"><Loader2 className="w-8 h-8 animate-spin text-slate-500" /></div>
      ) : companies.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="text-center py-16 text-slate-500">
            No companies yet. Run the migration above, or click "New Company".
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {companies.map(c => (
            <div
              key={c.id}
              onClick={() => setActiveId(c.id)}
              className={`relative bg-slate-900/60 hover:bg-slate-900 border rounded-lg p-4 pl-9 cursor-pointer transition-colors space-y-2 ${selected.has(c.id) ? 'border-violet-600' : 'border-slate-800'}`}
              data-testid={`company-card-${c.id}`}
            >
              <input
                type="checkbox"
                checked={selected.has(c.id)}
                onChange={(e) => toggleSelect(c.id, e)}
                onClick={(e) => e.stopPropagation()}
                className="absolute top-4 left-3 w-4 h-4 accent-violet-600 cursor-pointer"
                data-testid={`select-company-${c.id}`}
              />
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-white font-medium leading-tight line-clamp-2">{c.name}</p>
                {c.sector && (
                  <Badge variant="outline" className={`text-[10px] capitalize shrink-0 ${SECTOR_COLORS[c.sector] || 'border-slate-700 text-slate-400'}`}>
                    {c.sector.replace(/_/g, ' ')}
                  </Badge>
                )}
              </div>
              {(c.record_type || (c.supplier_tags && c.supplier_tags.length)) && (
                <div className="flex flex-wrap items-center gap-1">
                  <RecordTypeBadge type={c.record_type} />
                  <SupplierTagBadges tags={c.supplier_tags} />
                </div>
              )}
              {c.domain && <p className="text-xs text-slate-400 flex items-center gap-1"><Globe className="w-3 h-3" /> {c.domain}</p>}
              {c.location && <p className="text-xs text-slate-500 flex items-center gap-1 line-clamp-1"><MapPin className="w-3 h-3 shrink-0" /> {c.location}</p>}
              <div className="flex items-center gap-3 pt-1 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.contact_count} contacts</span>
                <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {c.deal_count} deals</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" className="bg-slate-900 border-slate-700" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="companies-prev">
            <ChevronLeft className="w-4 h-4" /> Prev
          </Button>
          <span className="text-sm text-slate-400">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" className="bg-slate-900 border-slate-700" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="companies-next">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      <CompanyDetailDialog companyId={activeId} sectors={sectors} onClose={() => setActiveId(null)} onChanged={fetchCompanies} />

      <ImportDialog objectType="company" open={showImport} onOpenChange={setShowImport} onDone={fetchCompanies} />
      <AddToListDialog objectType="company" ids={[...selected]} open={showAddToList} onOpenChange={setShowAddToList} onDone={() => setSelected(new Set())} />
      <CareSegmentBulkDialog ids={[...selected]} open={showBulkSegment} onOpenChange={setShowBulkSegment} onDone={() => { setSelected(new Set()); fetchCompanies(); }} />
      <ShareToOrgDialog objectType="company" ids={[...selected]} open={showShareOrg} onOpenChange={setShowShareOrg} onDone={() => setSelected(new Set())} />
    </div>
  );
};

const AddPersonForm = ({ companyId, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ full_name: '', job_title: '', email: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.full_name.trim()) return toast.error('Add a name');
    setBusy(true);
    try {
      await axios.post(`${API}/contacts`, { ...f, full_name: f.full_name.trim(), company_id: companyId, source: 'manual' });
      toast.success('Person added'); setF({ full_name: '', job_title: '', email: '', phone: '' }); setOpen(false); onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };
  if (!open) return <Button size="sm" variant="outline" className="h-7 mb-2" onClick={() => setOpen(true)} data-testid="company-add-person-btn"><UserPlus className="w-3.5 h-3.5 mr-1" />Add person</Button>;
  return (
    <div className="grid sm:grid-cols-2 gap-2 mb-3 p-3 border border-slate-700 rounded-md" data-testid="company-add-person-form">
      {[['full_name', 'Name *'], ['job_title', 'Job title, e.g. CEO'], ['email', 'Email'], ['phone', 'Phone']].map(([k, ph]) => (
        <Input key={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className="h-8 text-sm" data-testid={`company-add-person-${k}`} />))}
      <div className="sm:col-span-2 flex gap-2">
        <Button size="sm" onClick={save} disabled={busy} data-testid="company-add-person-save">{busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save person</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
};

const CompanyDetailDialog = ({ companyId, sectors, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [news, setNews] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [team, setTeam] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [addingTeam, setAddingTeam] = useState(false);

  useEffect(() => { setNews(null); }, [companyId]);
  useEffect(() => { setTeam(company?.team_members || null); }, [company]);

  const fetchTeam = async () => {
    if (!company) return;
    setTeamLoading(true);
    try {
      const { data } = await axios.post(`${API}/web-enrich/team/${company.id}`);
      setTeam(data.team_members || []);
      if (!(data.team_members || []).length) toast.info('No named staff found on the site (it may be JS-rendered).');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Team scrape failed'); }
    finally { setTeamLoading(false); }
  };

  const addTeamContacts = async () => {
    if (!team?.length) return;
    setAddingTeam(true);
    try {
      const { data } = await axios.post(`${API}/web-enrich/team/${company.id}/add-contacts`, { members: team });
      toast.success(`${data.created} contact${data.created === 1 ? '' : 's'} added${data.created < data.requested ? ` (${data.requested - data.created} already existed)` : ''}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add contacts'); }
    finally { setAddingTeam(false); }
  };

  const fetchNews = async () => {
    if (!company?.name) return;
    setNewsLoading(true);
    try {
      const { data } = await axios.get(`${API}/market-intel/news`, { params: { q: company.name } });
      setNews(data.items || []);
    } catch { toast.error('News fetch failed'); }
    finally { setNewsLoading(false); }
  };

  const load = useCallback(() => {
    if (!companyId) { setCompany(null); return; }
    setLoading(true);
    axios.get(`${API}/companies/${companyId}`)
      .then(res => setCompany(res.data))
      .catch(() => toast.error('Failed to load company'))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const reload = () => { load(); if (onChanged) onChanged(); };

  const sectorLabel = (val) => sectors.find(s => s.value === val)?.label || (val || '').replace(/_/g, ' ');

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${company.name}"? Contacts will be kept but unlinked.`)) return;
    try {
      await axios.delete(`${API}/companies/${company.id}`);
      toast.success('Company deleted');
      onClose();
      onChanged();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Dialog open={!!companyId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-detail-dialog">
        {loading || !company ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-violet-400" /> {company.name}
              </DialogTitle>
              <DialogDescription asChild>
                <div className="text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1">
                  {company.domain && <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {company.domain}</span>}
                  {company.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {company.location}</span>}
                  {company.sector && <Badge variant="outline" className={`text-[10px] capitalize ${SECTOR_COLORS[company.sector] || 'border-slate-700 text-slate-400'}`}>{sectorLabel(company.sector)}</Badge>}
                  <RecordTypeBadge type={company.record_type} />
                </div>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 mt-2">
              {/* Contacts */}
              <div>
                <Label className="text-slate-300 mb-2 block flex items-center gap-2"><Users className="w-4 h-4" /> Contacts ({company.contacts?.length || 0})</Label>
                <AddPersonForm companyId={company.id} onAdded={reload} />
                {(!company.contacts || company.contacts.length === 0) ? (
                  <p className="text-xs text-slate-600 italic">No contacts linked.</p>
                ) : (
                  <div className="space-y-2">
                    {company.contacts.map(ct => (
                      <button
                        type="button"
                        key={ct.id}
                        onClick={() => { onClose(); navigate(`/admin/contacts?open=${ct.id}`); }}
                        className="w-full text-left bg-slate-950 border border-slate-800 hover:border-violet-600/60 rounded-md p-3 transition-colors"
                        data-testid={`company-contact-${ct.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white hover:text-violet-300">{ct.full_name}</span>
                          {ct.job_title && <span className="text-[11px] text-slate-500">{ct.job_title}</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-400">
                          {ct.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {ct.email}</span>}
                          {ct.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {ct.phone}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Deals */}
              <div>
                <Label className="text-slate-300 mb-2 block flex items-center gap-2"><Target className="w-4 h-4" /> Deals ({company.deals?.length || 0})</Label>
                {(!company.deals || company.deals.length === 0) ? (
                  <p className="text-xs text-slate-600 italic">No deals yet.</p>
                ) : (
                  <div className="space-y-2">
                    {company.deals.map(d => (
                      <div key={d.id} className="bg-slate-950 border border-slate-800 rounded-md p-3 flex items-center justify-between">
                        <span className="text-sm text-white">{d.title}</span>
                        <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400 capitalize">{(d.stage || '').replace(/_/g, ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* News */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-300 flex items-center gap-2"><Newspaper className="w-4 h-4" /> Recent news</Label>
                  <Button size="sm" variant="outline" className="h-7 border-slate-700 text-slate-300 hover:bg-slate-800" onClick={fetchNews} disabled={newsLoading} data-testid="company-news-btn">
                    {newsLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Fetch
                  </Button>
                </div>
                {news === null ? (
                  <p className="text-xs text-slate-600 italic">Click Fetch to pull recent news mentions (free).</p>
                ) : news.length === 0 ? (
                  <p className="text-xs text-slate-600 italic">No recent news found.</p>
                ) : (
                  <ul className="space-y-2" data-testid="company-news-list">
                    {news.map((n, i) => (
                      <li key={i} className="text-xs">
                        <a href={n.link} target="_blank" rel="noreferrer" className="text-violet-400 hover:text-violet-300 flex items-start gap-1">
                          <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" /> {n.title}
                        </a>
                        <div className="text-slate-600 mt-0.5">{n.source}{n.published ? ` · ${n.published}` : ''}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Team / staff (website scrape) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-300 flex items-center gap-2"><UserSearch className="w-4 h-4" /> Team &amp; staff{team?.length ? ` (${team.length})` : ''}</Label>
                  <div className="flex gap-1.5">
                    {team?.length > 0 && (
                      <Button size="sm" variant="outline" className="h-7 border-slate-700 text-slate-300 hover:bg-slate-800" onClick={addTeamContacts} disabled={addingTeam} data-testid="company-add-team-contacts-btn">
                        {addingTeam ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />}Add as contacts
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-7 border-slate-700 text-slate-300 hover:bg-slate-800" onClick={fetchTeam} disabled={teamLoading || !(company.website || company.domain)} data-testid="company-scrape-team-btn">
                      {teamLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UserSearch className="w-3.5 h-3.5 mr-1" />}Find staff
                    </Button>
                  </div>
                </div>
                {!(company.website || company.domain) ? (
                  <p className="text-xs text-slate-600 italic">No website on file to scrape.</p>
                ) : team === null ? (
                  <p className="text-xs text-slate-600 italic">Click "Find staff" to scrape the company's team/about page for named people &amp; roles (free).</p>
                ) : team.length === 0 ? (
                  <p className="text-xs text-slate-600 italic">No named staff found.</p>
                ) : (
                  <div className="space-y-2" data-testid="company-team-list">
                    {team.map((m, i) => (
                      <div key={i} className="bg-slate-950 border border-slate-800 rounded-md p-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white">{m.name}</span>
                          {m.role && <span className="text-[11px] text-slate-500">{m.role}</span>}
                        </div>
                        {(m.email || m.linkedin) && (
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-400">
                            {m.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {m.email}</span>}
                            {m.linkedin && <a href={m.linkedin.startsWith('http') ? m.linkedin : `https://${m.linkedin}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-violet-400 hover:text-violet-300"><ExternalLink className="w-3 h-3" /> LinkedIn</a>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {company.website && (
                <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-violet-400 hover:text-violet-300">
                  <ExternalLink className="w-3 h-3" /> Visit website
                </a>
              )}

              <EmailHistory emails={company.emails} />
              <CareSegmentEditor company={company} onSaved={reload} />
              <TagsLifecycle objectType="company" recordId={company.id} tags={company.tags} lifecycle={company.lifecycle_stage} onSaved={reload} />
              <CustomFields objectType="company" recordId={company.id} value={company.custom_fields} onSaved={reload} />
              <ActivityTimeline parentType="company" parentId={company.id} />
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button variant="ghost" onClick={handleDelete} className="text-rose-400 hover:bg-rose-900/20" data-testid="delete-company-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
              <Button variant="outline" onClick={onClose} className="bg-slate-900 border-slate-700">Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Companies;
